import { useEffect, useRef, useState } from 'react';
import { useStore } from '../store/useStore.js';
import {
  X, Play, Pause, SkipForward, SkipBack, Volume2, VolumeX,
  CloudRain, Wind, Coffee, Radio, Music, Music2, Music3, Music4,
  Headphones, Flame, Waves, Bird, TreeDeciduous, Mountain, Snowflake, Zap,
  type LucideIcon,
} from 'lucide-react';

const ICONS: Record<string, LucideIcon> = {
  Volume2, CloudRain, Wind, Coffee, Radio, Music, Music2, Music3, Music4,
  Headphones, Flame, Waves, Bird, TreeDeciduous, Mountain, Snowflake, Zap,
};

interface Track {
  id: string;
  label: string;
  icon: string;
  url: string;
}

export function FocusAudioMenu() {
  const open = useStore((s) => s.focusAudioOpen);
  const setOpen = useStore((s) => s.setFocusAudioOpen);

  const [tracks, setTracks] = useState<Track[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [activeTrack, setActiveTrack] = useState<string | null>(null);
  const [playing, setPlaying] = useState(false);
  const [volume, setVolume] = useState(0.5);
  const [anchor, setAnchor] = useState<{ top: number; left: number } | null>(null);

  const panelRef = useRef<HTMLDivElement>(null);
  // Web Audio API gives true gapless loops (HTMLAudioElement.loop has a perceptible gap).
  const ctxRef = useRef<AudioContext | null>(null);
  const gainRef = useRef<GainNode | null>(null);
  const sourceRef = useRef<AudioBufferSourceNode | null>(null);
  const bufferCacheRef = useRef<Map<string, AudioBuffer>>(new Map());
  const loadingUrlRef = useRef<string | null>(null);

  function ensureContext(): { ctx: AudioContext; gain: GainNode } {
    let ctx = ctxRef.current;
    let gain = gainRef.current;
    if (!ctx) {
      const Ctor = window.AudioContext || (window as any).webkitAudioContext;
      ctx = new Ctor();
      ctxRef.current = ctx;
    }
    if (!gain) {
      gain = ctx.createGain();
      gain.gain.value = volume;
      gain.connect(ctx.destination);
      gainRef.current = gain;
    }
    return { ctx, gain };
  }

  function stopSource() {
    const src = sourceRef.current;
    if (src) {
      try { src.stop(); } catch { /* already stopped */ }
      try { src.disconnect(); } catch { /* ignore */ }
      sourceRef.current = null;
    }
  }

  async function loadBuffer(url: string): Promise<AudioBuffer | null> {
    const cached = bufferCacheRef.current.get(url);
    if (cached) return cached;
    try {
      const res = await fetch(url, { credentials: 'include' });
      if (!res.ok) return null;
      const arr = await res.arrayBuffer();
      const { ctx } = ensureContext();
      const buf = await ctx.decodeAudioData(arr.slice(0));
      bufferCacheRef.current.set(url, buf);
      return buf;
    } catch {
      return null;
    }
  }

  // Restore persisted state on mount.
  useEffect(() => {
    const saved = localStorage.getItem('synapse_focus_state');
    if (saved) {
      try {
        const { track, vol } = JSON.parse(saved);
        if (track) setActiveTrack(track);
        if (typeof vol === 'number') setVolume(vol);
      } catch { /* ignore */ }
    }
    return () => {
      stopSource();
      gainRef.current?.disconnect();
      ctxRef.current?.close().catch(() => { /* ignore */ });
    };
  }, []);

  // Fetch library when menu opens (always refresh so admin edits show up).
  useEffect(() => {
    if (!open && tracks.length > 0) return;
    let cancelled = false;
    setLoading(true);
    setLoadErr(null);
    fetch('/api/focus-audio', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`http_${r.status}`))))
      .then((data: { tracks: Track[] }) => {
        if (!cancelled) setTracks(data.tracks);
      })
      .catch((err) => {
        if (!cancelled) setLoadErr(err?.message ?? 'load_failed');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Anchor next to the sidebar trigger button.
  useEffect(() => {
    if (!open) return;
    const btn = document.querySelector<HTMLElement>('[data-focus-audio-trigger]');
    const rect = btn?.getBoundingClientRect();
    if (rect) {
      setAnchor({ top: rect.top, left: rect.right + 8 });
    } else {
      setAnchor({ top: 120, left: 60 });
    }
  }, [open]);

  // Click outside → close. Ignore clicks on trigger so toggling still works.
  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      if (!panelRef.current) return;
      if (panelRef.current.contains(e.target as Node)) return;
      const trigger = (e.target as HTMLElement)?.closest('[data-focus-audio-trigger]');
      if (trigger) return;
      setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, setOpen]);

  // Volume + persistence — apply gain immediately.
  useEffect(() => {
    if (gainRef.current && ctxRef.current) {
      gainRef.current.gain.setTargetAtTime(volume, ctxRef.current.currentTime, 0.02);
    }
    localStorage.setItem('synapse_focus_state', JSON.stringify({ track: activeTrack, vol: volume }));
  }, [volume, activeTrack]);

  // Drive playback via Web Audio API for true gapless loops.
  useEffect(() => {
    let cancelled = false;
    async function run() {
      if (!activeTrack || !playing) {
        stopSource();
        return;
      }
      const track = tracks.find((t) => t.id === activeTrack);
      if (!track) return;
      const url = track.url;
      // Avoid re-loading the same buffer if already playing it.
      const currentBuffer = sourceRef.current && (sourceRef.current as any)._url === url;
      if (currentBuffer) return;

      const { ctx, gain } = ensureContext();
      // Browsers require user gesture to resume context.
      if (ctx.state === 'suspended') {
        try { await ctx.resume(); } catch { /* ignore */ }
      }

      loadingUrlRef.current = url;
      const buffer = await loadBuffer(url);
      if (cancelled) return;
      if (!buffer) {
        setPlaying(false);
        return;
      }
      // If another track was selected during the load, abort.
      if (loadingUrlRef.current !== url) return;

      stopSource();
      const src = ctx.createBufferSource();
      src.buffer = buffer;
      src.loop = true;
      src.connect(gain);
      (src as any)._url = url;
      src.start();
      sourceRef.current = src;
    }
    void run();
    return () => { cancelled = true; };
  }, [activeTrack, playing, tracks]);

  function skip(dir: 1 | -1) {
    if (tracks.length === 0) return;
    const idx = activeTrack ? tracks.findIndex((t) => t.id === activeTrack) : -1;
    const next = ((idx + dir) % tracks.length + tracks.length) % tracks.length;
    const target = tracks[next];
    if (target) {
      setActiveTrack(target.id);
      setPlaying(true);
    }
  }

  if (!open) return null;
  if (!anchor) return null;

  const active = tracks.find((t) => t.id === activeTrack) ?? null;
  const ActiveIcon = active ? ICONS[active.icon] ?? Headphones : Headphones;

  return (
    <div
      ref={panelRef}
      role="dialog"
      aria-label="Sons de Foco"
      style={{
        position: 'fixed',
        top: Math.max(60, anchor.top - 8),
        left: anchor.left,
        width: 280,
        zIndex: 250,
        background: 'var(--panel)',
        border: '1px solid var(--panel-border)',
        borderRadius: 'var(--r-lg)',
        boxShadow: '0 16px 48px rgba(0,0,0,0.55), 0 0 18px rgba(var(--neon-rgb), 0.10)',
        backdropFilter: 'blur(28px) saturate(160%)',
        WebkitBackdropFilter: 'blur(28px) saturate(160%)',
        animation: 'pop-in .18s var(--ease-out)',
        fontFamily: 'var(--font-display)',
        overflow: 'hidden',
      }}
    >
      <div style={headerStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
          <div style={badgeStyle}><Headphones size={14} /></div>
          <span style={titleStyle}>Sons de Foco</span>
        </div>
        <button onClick={() => setOpen(false)} aria-label="Fechar" style={closeBtnStyle}>
          <X size={14} />
        </button>
      </div>

      {/* Now playing controls */}
      <div style={playerStyle}>
        <button onClick={() => skip(-1)} aria-label="Anterior" style={iconBtnStyle} disabled={tracks.length === 0}>
          <SkipBack size={15} />
        </button>
        <button
          onClick={() => {
            if (!active && tracks.length > 0) {
              setActiveTrack(tracks[0]!.id);
              setPlaying(true);
            } else {
              setPlaying((p) => !p);
            }
          }}
          aria-label={playing ? 'Pausar' : 'Tocar'}
          style={playBtnStyle(!!active || tracks.length > 0)}
          disabled={tracks.length === 0}
        >
          {playing ? <Pause size={16} fill="currentColor" /> : <Play size={16} fill="currentColor" />}
        </button>
        <button onClick={() => skip(1)} aria-label="Próximo" style={iconBtnStyle} disabled={tracks.length === 0}>
          <SkipForward size={15} />
        </button>
        <div style={nowPlayingStyle}>
          <ActiveIcon size={13} style={{ flexShrink: 0, color: active ? 'var(--neon)' : 'var(--muted)' }} />
          <span style={trackNameStyle}>
            {active ? active.label : 'Selecione uma faixa'}
          </span>
        </div>
      </div>

      {/* Volume */}
      <div style={volumeRowStyle}>
        <button onClick={() => setVolume(volume > 0 ? 0 : 0.5)} aria-label="Volume" style={mutedBtnStyle}>
          {volume > 0 ? <Volume2 size={13} /> : <VolumeX size={13} />}
        </button>
        <input
          type="range"
          min="0" max="1" step="0.01"
          value={volume}
          onChange={(e) => setVolume(parseFloat(e.target.value))}
          aria-label="Volume"
          style={sliderStyle}
        />
      </div>

      {/* Track list */}
      <div style={listStyle}>
        {loading && tracks.length === 0 && (
          <div style={emptyStyle}>Carregando…</div>
        )}
        {!loading && loadErr && tracks.length === 0 && (
          <div style={{ ...emptyStyle, color: '#fca5a5' }}>Erro: {loadErr}</div>
        )}
        {!loading && !loadErr && tracks.length === 0 && (
          <div style={emptyStyle}>
            Nenhuma faixa.<br />
            <span style={{ opacity: 0.7 }}>Admin adiciona em /focus-audio.</span>
          </div>
        )}
        {tracks.map((track) => {
          const isActive = activeTrack === track.id;
          const Icon = ICONS[track.icon] ?? Volume2;
          return (
            <button
              key={track.id}
              onClick={() => {
                if (isActive) setPlaying((p) => !p);
                else { setActiveTrack(track.id); setPlaying(true); }
              }}
              style={trackBtnStyle(isActive)}
            >
              <Icon size={14} style={{ flexShrink: 0 }} />
              <span style={{ flex: 1, textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {track.label}
              </span>
              {isActive && playing && (
                <span style={pulseDot} />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

const headerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 8,
  padding: '10px 12px',
  borderBottom: '1px solid var(--panel-border)',
  background: 'linear-gradient(180deg, rgba(var(--neon-rgb), 0.06), transparent)',
};

const badgeStyle: React.CSSProperties = {
  width: 24, height: 24,
  borderRadius: 7,
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  background: 'rgba(var(--neon-rgb), 0.12)',
  border: '1px solid rgba(var(--neon-rgb), 0.28)',
  color: 'var(--neon)',
  boxShadow: '0 0 12px rgba(var(--neon-rgb), 0.22)',
  flexShrink: 0,
};

const titleStyle: React.CSSProperties = {
  fontSize: 12.5,
  fontWeight: 700,
  letterSpacing: '-0.005em',
  color: 'var(--text)',
};

const closeBtnStyle: React.CSSProperties = {
  width: 24, height: 24,
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  background: 'transparent',
  border: '1px solid var(--panel-border)',
  borderRadius: 7,
  color: 'var(--muted)',
  cursor: 'pointer',
};

const playerStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'auto auto auto 1fr',
  alignItems: 'center',
  gap: 6,
  padding: '10px 12px 4px',
};

const nowPlayingStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 6,
  padding: '0 4px 0 8px',
  minWidth: 0,
};

const trackNameStyle: React.CSSProperties = {
  fontSize: 11.5,
  color: 'var(--text-dim, var(--text))',
  letterSpacing: '-0.005em',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const iconBtnStyle: React.CSSProperties = {
  width: 28, height: 28,
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  background: 'transparent',
  border: '1px solid var(--panel-border)',
  borderRadius: 8,
  color: 'var(--text-dim, var(--text))',
  cursor: 'pointer',
  transition: 'all .15s',
  flexShrink: 0,
};

function playBtnStyle(canPlay: boolean): React.CSSProperties {
  return {
    width: 32, height: 32,
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    background: canPlay ? 'var(--neon)' : 'rgba(var(--neon-rgb), 0.16)',
    color: canPlay ? '#02131a' : 'rgba(var(--neon-rgb), 0.5)',
    border: 'none',
    borderRadius: 9,
    cursor: canPlay ? 'pointer' : 'not-allowed',
    boxShadow: canPlay ? '0 0 14px rgba(var(--neon-rgb), 0.32)' : 'none',
    flexShrink: 0,
  };
}

const volumeRowStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 8,
  padding: '4px 12px 10px',
  borderBottom: '1px solid var(--panel-border)',
};

const mutedBtnStyle: React.CSSProperties = {
  width: 24, height: 24,
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  background: 'transparent', border: 'none',
  color: 'var(--muted)', cursor: 'pointer',
  flexShrink: 0,
};

const sliderStyle: React.CSSProperties = {
  flex: 1,
  height: 3,
  background: 'rgba(var(--neon-rgb), 0.18)',
  borderRadius: 999,
  appearance: 'none',
  outline: 'none',
  accentColor: 'var(--neon)',
  cursor: 'pointer',
};

const listStyle: React.CSSProperties = {
  display: 'flex', flexDirection: 'column', gap: 2,
  padding: '6px 6px 8px',
  maxHeight: 240,
  overflowY: 'auto',
};

function trackBtnStyle(isActive: boolean): React.CSSProperties {
  return {
    display: 'flex', alignItems: 'center', gap: 9,
    padding: '7px 9px',
    background: isActive ? 'rgba(var(--neon-rgb), 0.10)' : 'transparent',
    border: '1px solid ' + (isActive ? 'rgba(var(--neon-rgb), 0.32)' : 'transparent'),
    borderRadius: 8,
    color: isActive ? 'var(--neon)' : 'var(--text)',
    fontSize: 12,
    cursor: 'pointer',
    transition: 'background .12s, color .12s, border-color .12s',
    fontFamily: 'inherit',
    width: '100%',
  };
}

const emptyStyle: React.CSSProperties = {
  fontSize: 11.5,
  color: 'var(--muted)',
  textAlign: 'center',
  padding: '16px 12px',
  lineHeight: 1.5,
};

const pulseDot: React.CSSProperties = {
  width: 6, height: 6, borderRadius: '50%',
  background: 'var(--neon)',
  boxShadow: '0 0 8px var(--neon)',
  animation: 'pulse-dot 1.4s ease-in-out infinite',
};
